import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  DeviceUnknown as DeviceIcon,
} from '@mui/icons-material';
import { webauthnService, WebAuthnCredential } from '../services/webauthn';

const WebAuthnSettings: React.FC = () => {
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  
  // Registration dialog
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [deviceName, setDeviceName] = useState('');

  useEffect(() => {
    checkSupport();
    loadCredentials();
  }, []);

  const checkSupport = async () => {
    try {
      const supported = webauthnService.isSupported();
      setIsSupported(supported);
      
      if (supported) {
        const available = await webauthnService.isBiometricAvailable();
        setIsAvailable(available);
      }
    } catch (error) {
      console.error('Error checking WebAuthn support:', error);
      setIsSupported(false);
      setIsAvailable(false);
    }
  };

  const loadCredentials = async () => {
    try {
      const creds = await webauthnService.getCredentials();
      
      // Ensure we always set an array
      if (Array.isArray(creds)) {
        setCredentials(creds);
      } else {
        console.warn('Credentials response is not an array:', creds);
        setCredentials([]);
      }
    } catch (error: any) {
      console.error('Error loading credentials:', error);
      // If API call fails, set empty array and show error message
      setCredentials([]);
      if (error.response?.status !== 404) {
        // Only show error if it's not a "no credentials found" error
        setError('Failed to load registered devices');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!deviceName.trim()) {
      setError('Please enter a name for this device');
      return;
    }

    setError('');
    setSuccess('');
    setRegistering(true);

    try {
      const result = await webauthnService.registerCredential(deviceName.trim());
      
      if (result.success) {
        setSuccess('Device registered successfully!');
        setRegisterDialogOpen(false);
        setDeviceName('');
        await loadCredentials(); // Reload the list
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (error: any) {
      setError(error.message || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (credentialId: string) => {
    setError('');
    setSuccess('');
    setDeleting(credentialId);

    try {
      const result = await webauthnService.deleteCredential(credentialId);
      
      if (result.success) {
        setSuccess('Device removed successfully!');
        await loadCredentials(); // Reload the list
      } else {
        setError(result.message || 'Failed to remove device');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to remove device');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'Unknown date';
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenRegisterDialog = () => {
    setRegisterDialogOpen(true);
    setDeviceName('');
    setError('');
    setSuccess('');
  };

  if (!isSupported) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Biometric Authentication</Typography>
          </Box>
          <Alert severity="warning">
            Your browser doesn't support biometric authentication. Please use a modern browser 
            like Chrome, Firefox, Safari, or Edge to enable this feature.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!isAvailable) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Biometric Authentication</Typography>
          </Box>
          <Alert severity="info">
            Your device doesn't support biometric authentication or no biometric sensors 
            were detected. You may need to set up fingerprint or face recognition in your 
            device settings first.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <FingerprintIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Biometric Authentication</Typography>
            <Chip
              label="Secure"
              size="small"
              color="success"
              sx={{ ml: 1 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenRegisterDialog}
            disabled={registering}
          >
            Add Device
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Use your device's biometric sensors (fingerprint, face recognition, etc.) to 
          securely sign in to your Fojourn account. Your biometric data never leaves your device.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : !Array.isArray(credentials) ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading biometric devices. Please refresh the page.
          </Alert>
        ) : credentials.length === 0 ? (
          <Box textAlign="center" py={3}>
            <DeviceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No biometric devices registered
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Add a device to enable quick and secure biometric sign-in
            </Typography>
          </Box>
        ) : (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Registered Devices ({credentials.length})
            </Typography>
            <List>
              {credentials.filter(credential => credential && credential.id).map((credential) => (
                <ListItem key={credential.id} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <FingerprintIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                        {credential.name || 'Unnamed Device'}
                      </Box>
                    }
                    secondary={`Registered on ${formatDate(credential.created_at)}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Remove device">
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(credential.id)}
                        disabled={deleting === credential.id}
                        color="error"
                      >
                        {deleting === credential.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>

      {/* Registration Dialog */}
      <Dialog
        open={registerDialogOpen}
        onClose={() => !registering && setRegisterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FingerprintIcon sx={{ mr: 1 }} />
            Register Biometric Device
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Give your device a memorable name and follow the prompts to register 
            your biometric authentication.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Device Name"
            fullWidth
            variant="outlined"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="e.g., iPhone Touch ID, MacBook Pro"
            disabled={registering}
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRegisterDialogOpen(false)}
            disabled={registering}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegister}
            variant="contained"
            disabled={registering || !deviceName.trim()}
            startIcon={registering ? <CircularProgress size={16} /> : <FingerprintIcon />}
          >
            {registering ? 'Registering...' : 'Register Device'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WebAuthnSettings;